/**
 * Stage 3 client calls for one lead: the SMS/Slack message log that the
 * Activity tab interleaves with events, and the assessment invite send.
 * Query key: ['lead', id, 'messages'].
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/lib/api-client';
import { LEADS_KEY, leadKey } from '@/features/pipeline/api';
import type { AssessmentSummary, Message } from '@/features/pipeline/types';

export const leadMessagesKey = (id: string) => [...leadKey(id), 'messages'] as const;

/** The list endpoint may answer a bare array or `{ items }`. */
function unwrapList<T>(res: T[] | { items: T[] } | null | undefined): T[] {
  if (!res) return [];
  return Array.isArray(res) ? res : res.items ?? [];
}

export function useLeadMessages(leadId: string | null) {
  const client = useApiClient();
  return useQuery({
    queryKey: leadMessagesKey(leadId ?? ''),
    queryFn: async () =>
      unwrapList(await client.get<Message[] | { items: Message[] }>(`/api/leads/${leadId}/messages`)),
    enabled: !!leadId,
    // A queued text settles when the cron drains it; poll gently while one is pending.
    refetchInterval: (query) => (query.state.data?.some((m) => m.status === 'queued') ? 15_000 : false),
  });
}

/** `POST /api/leads/:id/assessment/send` — first send or resend; refetches the lead, its messages and the board. */
export function useSendAssessment(leadId: string) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      client.post<{ assessment?: AssessmentSummary | null } | AssessmentSummary | null>(
        `/api/leads/${leadId}/assessment/send`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leadKey(leadId) });
      void qc.invalidateQueries({ queryKey: LEADS_KEY });
    },
  });
}
